<!--
  new_dtime_frontend_vue_2026/src/views/accountManagement/ForgotPasswordFlow.vue
  ไฟล์ใหม่ — แทนที่ ForgotPass.vue + ForgotResetPass.vue เดิม ด้วย flow เดียวที่มี OTP SMS

  Route แนะนำ (ใช้ path เดิม ไม่ต้องเปลี่ยนที่ไหนอื่น):
    { path: "/forgot-password", name: "forgot-password", component: ForgotPasswordFlow }
-->
<template>
  <v-row class="justify-center align-center fp-wrapper" no-gutters>
    <v-col cols="12" sm="10" md="6" lg="5" xl="4">
      <v-card class="fp-card" elevation="2">
        <!-- ======================= Header ======================= -->
        <div class="fp-header">
          <div class="fp-header-top">
            <div class="fp-logo"><span class="fp-logo-d">D</span> DTime</div>
            <div class="fp-lang">
              EN <v-icon small color="white">mdi-chevron-down</v-icon>
            </div>
          </div>

          <div class="fp-header-body" v-if="step !== 4">
            <h2 class="fp-title">{{ headerTitle }}</h2>
            <p class="fp-desc">{{ headerDesc }}</p>
          </div>

          <!-- Step 4: success icon อยู่ในโซน header -->
          <div class="fp-header-body fp-success-icon-zone" v-else>
            <div class="fp-success-circle">
              <v-icon color="#2e7d32" size="46">mdi-check</v-icon>
            </div>
            <h2 class="fp-title">Password reset successful</h2>
          </div>
        </div>

        <!-- ======================= Body ======================= -->
        <v-card-text class="fp-body">
          <!-- STEP 1: Forgot Password -->
          <div v-if="step === 1">
            <label class="fp-label">Company Code</label>
            <v-text-field
              v-model="companyCode"
              placeholder="Enter your company code"
              outlined
              dense
              hide-details="auto"
              class="mb-4"
            />

            <label class="fp-label">Phone Number</label>
            <v-row no-gutters class="mb-2">
              <v-col cols="4">
                <v-select
                  :items="[{ text: '🇹🇭 +66', value: '+66' }]"
                  v-model="phoneCountry"
                  outlined
                  dense
                  hide-details
                  class="fp-country-select"
                />
              </v-col>
              <v-col cols="8">
                <v-text-field
                  v-model="phone"
                  placeholder="Enter your registered phone number"
                  outlined
                  dense
                  hide-details="auto"
                  maxlength="10"
                  class="fp-phone-input"
                />
              </v-col>
            </v-row>

            <v-alert
              v-if="errorMessage"
              dense
              text
              type="error"
              class="mt-3 mb-0"
            >
              {{ errorMessage }}
            </v-alert>

            <v-btn
              block
              rounded
              dark
              color="#0A7EA5"
              class="fp-btn mt-6"
              :loading="loading"
              @click="onSendOtp"
            >
              Send OTP
            </v-btn>

            <div class="text-center mt-4">
              <span class="fp-link" @click="$router.push('/login')"
                >Back to Sign In</span
              >
            </div>
          </div>

          <!-- STEP 2: Verify OTP -->
          <div v-else-if="step === 2">
            <!-- ปกติ: กรอก OTP -->
            <template v-if="otpState === 'input'">
              <div class="text-center mb-2 fp-otp-caption">
                Enter the 6-digit code sent to<br />
                <strong>{{ phoneMasked }}</strong>
              </div>

              <v-otp-input
                length="6"
                v-model="otpCode"
                class="fp-otp-input"
                @finish="onOtpFinish"
              ></v-otp-input>

              <div class="text-center mt-2 fp-otp-meta">
                Code expires in
                <strong :class="{ 'red--text': countdown <= 30 }">{{
                  countdownDisplay
                }}</strong>
              </div>

              <div class="text-center mt-1">
                <span v-if="resendCountdown > 0" class="fp-link-disabled"
                  >Resend code ({{ resendCountdown }}s)</span
                >
                <span v-else class="fp-link" @click="onResend"
                  >Resend code</span
                >
              </div>

              <div class="text-center mt-1">
                <span class="fp-link" @click="step = 1"
                  >Change phone number</span
                >
              </div>

              <v-alert
                v-if="errorMessage"
                dense
                text
                type="error"
                class="mt-4 mb-0"
              >
                {{ errorMessage }}
                <span v-if="attemptsRemaining !== null">
                  You have {{ attemptsRemaining }} attempts remaining.
                </span>
              </v-alert>

              <v-btn
                block
                rounded
                dark
                color="#0A7EA5"
                class="fp-btn mt-6"
                :loading="loading"
                :disabled="otpCode.length !== 6"
                @click="onVerifyOtp"
              >
                Verify
              </v-btn>
            </template>

            <!-- Error flow: OTP Expired -->
            <template v-else-if="otpState === 'expired'">
              <div class="text-center">
                <v-icon size="48" color="#d32f2f"
                  >mdi-clock-alert-outline</v-icon
                >
                <h3 class="mt-3">OTP expired</h3>
                <p class="fp-desc-body">The verification code has expired.</p>
              </div>
              <v-btn
                block
                rounded
                dark
                color="#0A7EA5"
                class="fp-btn mt-4"
                @click="onResend"
              >
                Resend code
              </v-btn>
              <v-btn
                block
                outlined
                rounded
                color="#0A7EA5"
                class="mt-3"
                @click="step = 1"
              >
                Back
              </v-btn>
            </template>

            <!-- Error flow: Too Many Attempts -->
            <template v-else-if="otpState === 'locked'">
              <div class="text-center">
                <v-icon size="48" color="#d32f2f"
                  >mdi-lock-alert-outline</v-icon
                >
                <h3 class="mt-3">Too many attempts</h3>
                <p class="fp-desc-body">
                  You've entered an incorrect code too many times.<br />
                  Please wait at least 10 minutes to request a new verification
                  code.
                </p>
              </div>
              <v-alert v-if="errorMessage" dense text type="error" class="mb-0">
                {{ errorMessage }}
              </v-alert>
              <v-btn
                block
                rounded
                dark
                color="#0A7EA5"
                class="fp-btn mt-4"
                :loading="loading"
                @click="onResend"
              >
                Resend OTP
              </v-btn>
            </template>
          </div>

          <!-- STEP 3: Create New Password -->
          <div v-else-if="step === 3">
            <label class="fp-label">New Password</label>
            <v-text-field
              v-model="newPassword"
              :append-icon="showPassword1 ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="showPassword1 = !showPassword1"
              :type="showPassword1 ? 'text' : 'password'"
              outlined
              dense
              hide-details="auto"
              class="mb-1"
            />
            <v-progress-linear
              :value="strengthPercent"
              :color="strengthColor"
              height="6"
              rounded
              class="mb-1"
            />
            <div
              class="text-right fp-strength-label"
              :style="{ color: strengthColor }"
            >
              {{ newPassword ? strengthLabel : "" }}
            </div>

            <label class="fp-label mt-3 d-block">Confirm New Password</label>
            <v-text-field
              v-model="confirmPassword"
              :append-icon="showPassword2 ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="showPassword2 = !showPassword2"
              :type="showPassword2 ? 'text' : 'password'"
              outlined
              dense
              hide-details="auto"
              class="mb-3"
            />

            <div class="fp-req-item" :class="{ 'fp-req-ok': rule.len }">
              <v-icon small :color="rule.len ? '#2e7d32' : '#bdbdbd'"
                >mdi-check-circle</v-icon
              >
              At least 8 characters
            </div>
            <div class="fp-req-item" :class="{ 'fp-req-ok': rule.hasNumber }">
              <v-icon small :color="rule.hasNumber ? '#2e7d32' : '#bdbdbd'"
                >mdi-check-circle</v-icon
              >
              Contains at least 1 number
            </div>
            <div class="fp-req-item" :class="{ 'fp-req-ok': rule.notCommon }">
              <v-icon small :color="rule.notCommon ? '#2e7d32' : '#bdbdbd'"
                >mdi-check-circle</v-icon
              >
              Avoid common passwords
            </div>
            <div class="fp-req-item" :class="{ 'fp-req-ok': rule.match }">
              <v-icon small :color="rule.match ? '#2e7d32' : '#bdbdbd'"
                >mdi-check-circle</v-icon
              >
              Passwords must match
            </div>

            <v-alert
              v-if="errorMessage"
              dense
              text
              type="error"
              class="mt-4 mb-0"
            >
              {{ errorMessage }}
            </v-alert>

            <v-btn
              block
              rounded
              dark
              color="#0A7EA5"
              class="fp-btn mt-6"
              :loading="loading"
              :disabled="!canSubmitPassword"
              @click="onResetPassword"
            >
              Reset Password
            </v-btn>
          </div>

          <!-- STEP 4: Reset Successful -->
          <div v-else-if="step === 4" class="text-center">
            <p class="fp-desc-body">
              Your password has been updated successfully.<br />
              For your security, all active sessions have been signed out.<br />
              Please sign in again using your new password.
            </p>
            <v-btn
              block
              rounded
              dark
              color="#0A7EA5"
              class="fp-btn mt-4"
              @click="$router.push('/login')"
            >
              Back to Sign In
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import forgotPasswordApi from "@/services/api-dtime/api_forgot_password.js";

const COMMON_PASSWORDS = [
  "12345678",
  "123456789",
  "1234567890",
  "password",
  "password1",
  "password123",
  "11111111",
  "00000000",
  "qwertyui",
  "qwerty123",
  "abc12345",
  "letmein1",
  "iloveyou",
  "admin123",
  "welcome1",
  "changeme",
];

export default {
  name: "ForgotPasswordFlow",
  mounted() {
    // if (api.isLoggedIn()) {
    //   this.$router.push("/dashboard");
    // }

    this.$hideLoader();
  },
  data() {
    return {
      step: 1,
      loading: false,
      errorMessage: "",

      // step 1
      companyCode: "",
      phoneCountry: "+66",
      phone: "",

      // step 2
      sessionId: "",
      phoneMasked: "",
      otpCode: "",
      otpState: "input", // input | expired | locked
      attemptsRemaining: null,
      countdown: 300, // 5 นาที
      countdownTimer: null,
      resendCountdown: 45,
      resendTimer: null,

      // step 3
      resetToken: "",
      newPassword: "",
      confirmPassword: "",
      showPassword1: false,
      showPassword2: false,
    };
  },
  computed: {
    headerTitle() {
      if (this.step === 1) return "Forgot your password?";
      if (this.step === 2) return "Verify your identity";
      if (this.step === 3) return "Create a new password";
      return "";
    },
    headerDesc() {
      if (this.step === 1)
        return "Enter your company code and your registered phone number to reset your password.";
      if (this.step === 2) return `Enter the 6-digit code we sent to`;
      if (this.step === 3)
        return "Your new password must be different from your previous passwords.";
      return "";
    },
    countdownDisplay() {
      const m = Math.floor(this.countdown / 60)
        .toString()
        .padStart(2, "0");
      const s = (this.countdown % 60).toString().padStart(2, "0");
      return `${m}:${s}`;
    },
    rule() {
      return {
        len: this.newPassword.length >= 8,
        hasNumber: /\d/.test(this.newPassword || ""),
        notCommon: !COMMON_PASSWORDS.includes(
          (this.newPassword || "").toLowerCase(),
        ),
        match:
          this.newPassword.length > 0 &&
          this.newPassword === this.confirmPassword,
      };
    },
    canSubmitPassword() {
      return (
        this.rule.len &&
        this.rule.hasNumber &&
        this.rule.notCommon &&
        this.rule.match
      );
    },
    strengthScore() {
      const p = this.newPassword || "";
      let score = 0;
      if (p.length >= 8) score++;
      if (p.length >= 12) score++;
      if (/[a-z]/.test(p) && /[A-Z]/.test(p)) score++;
      if (/\d/.test(p)) score++;
      if (/[^A-Za-z0-9]/.test(p)) score++;
      return score; // 0-5
    },
    strengthPercent() {
      if (!this.newPassword) return 0;
      return Math.min(100, (this.strengthScore / 5) * 100);
    },
    strengthLabel() {
      if (this.strengthScore <= 2) return "Weak";
      if (this.strengthScore <= 3) return "Fair";
      return "Strong";
    },
    strengthColor() {
      if (this.strengthScore <= 2) return "#d32f2f";
      if (this.strengthScore <= 3) return "#f9a825";
      return "#2e7d32";
    },
  },
  beforeDestroy() {
    this.clearTimers();
  },
  methods: {
    clearTimers() {
      if (this.countdownTimer) clearInterval(this.countdownTimer);
      if (this.resendTimer) clearInterval(this.resendTimer);
    },
    startOtpTimers() {
      this.clearTimers();
      this.countdown = 300;
      this.resendCountdown = 45;
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--;
        } else {
          clearInterval(this.countdownTimer);
          this.otpState = "expired";
        }
      }, 1000);
      this.resendTimer = setInterval(() => {
        if (this.resendCountdown > 0) this.resendCountdown--;
        else clearInterval(this.resendTimer);
      }, 1000);
    },

    // ==================== STEP 1 ====================
    async onSendOtp() {
      this.errorMessage = "";
      if (!this.companyCode.trim()) {
        this.errorMessage = "Please input Company Code";
        return;
      }
      if (!this.phone.trim()) {
        this.errorMessage = "Please input Phone Number";
        return;
      }

      this.loading = true;
      try {
        const res = await forgotPasswordApi.requestOtp(
          this.companyCode.trim(),
          this.phone.trim(),
        );
        this.loading = false;

        if (res.data && res.data.success) {
          this.sessionId = res.data.session_id;
          this.phoneMasked = res.data.phone_masked || this.phone;
          this.otpCode = "";
          this.otpState = "input";
          this.attemptsRemaining = null;
          this.step = 2;
          this.startOtpTimers();
        } else {
          this.errorMessage =
            (res.data && res.data.message) || "ส่ง OTP ไม่สำเร็จ";
        }
      } catch (error) {
        this.loading = false;
        this.errorMessage = "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
      }
    },

    // ==================== STEP 2 ====================
    onOtpFinish(code) {
      this.otpCode = code;
    },
    async onVerifyOtp() {
      this.errorMessage = "";
      this.loading = true;
      try {
        const res = await forgotPasswordApi.verifyOtp(
          this.sessionId,
          this.otpCode,
        );
        this.loading = false;
        const data = res.data;

        if (data && data.success) {
          this.clearTimers();
          this.resetToken = data.reset_token;
          this.newPassword = "";
          this.confirmPassword = "";
          this.step = 3;
          return;
        }

        if (data && data.code === "OTP_EXPIRED") {
          this.otpState = "expired";
          this.clearTimers();
          return;
        }
        if (data && data.code === "TOO_MANY_ATTEMPTS") {
          this.otpState = "locked";
          this.clearTimers();
          return;
        }

        this.attemptsRemaining =
          typeof data.attempts_remaining === "number"
            ? data.attempts_remaining
            : null;
        this.errorMessage =
          (data && data.message) || "Invalid verification code.";
        this.otpCode = "";
      } catch (error) {
        this.loading = false;
        this.errorMessage = "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
      }
    },
    async onResend() {
      this.errorMessage = "";
      this.loading = true;
      try {
        const res = await forgotPasswordApi.resendOtp(this.sessionId);
        this.loading = false;
        const data = res.data;

        if (data && data.success) {
          this.otpCode = "";
          this.attemptsRemaining = null;
          this.otpState = "input";
          this.startOtpTimers();
          return;
        }

        if (data && data.code === "TOO_MANY_ATTEMPTS") {
          // ⭐ ยังไม่ครบ 10 นาที -> คง state locked ไว้ พร้อมข้อความเวลาที่เหลือ
          this.otpState = "locked";
          this.errorMessage =
            data.message || "Please wait before requesting a new code.";
          return;
        }

        this.errorMessage = (data && data.message) || "ส่ง OTP ใหม่ไม่สำเร็จ";
      } catch (error) {
        this.loading = false;
        this.errorMessage = "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
      }
    },

    // ==================== STEP 3 ====================
    async onResetPassword() {
      this.errorMessage = "";
      if (!this.canSubmitPassword) return;

      this.loading = true;
      try {
        const res = await forgotPasswordApi.resetPassword(
          this.resetToken,
          this.newPassword,
          this.confirmPassword,
        );
        this.loading = false;

        if (res.data && res.data.success) {
          this.step = 4;
        } else {
          this.errorMessage =
            (res.data && res.data.message) || "รีเซ็ตรหัสผ่านไม่สำเร็จ";
          if (res.data && res.data.code === "RESET_TOKEN_INVALID") {
            // session/token หมดอายุระหว่างกรอก -> กลับไปเริ่มใหม่
            setTimeout(() => {
              this.step = 1;
            }, 2000);
          }
        }
      } catch (error) {
        this.loading = false;
        this.errorMessage = "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง";
      }
    },
  },
};
</script>

<style scoped>
.fp-wrapper {
  min-height: 100vh;
  background: #f4f6f8;
  padding: 24px 12px;
}
.fp-card {
  border-radius: 12px;
  overflow: hidden;
}
.fp-header {
  background: linear-gradient(135deg, #0d4f63, #0a7ea5);
  padding: 20px 28px 28px;
  color: #fff;
}
.fp-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  font-size: 15px;
}
.fp-logo-d {
  display: inline-block;
  background: #fff;
  color: #0a7ea5;
  border-radius: 4px;
  padding: 0 5px;
  margin-right: 2px;
}
.fp-lang {
  font-size: 13px;
  opacity: 0.9;
  cursor: pointer;
}
.fp-header-body {
  margin-top: 22px;
}
.fp-title {
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 6px;
}
.fp-desc {
  font-size: 13px;
  opacity: 0.9;
  margin: 0;
}
.fp-success-icon-zone {
  text-align: center;
}
.fp-success-circle {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: #e8f5e9;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
}
.fp-body {
  padding: 28px !important;
}
.fp-label {
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
}
.fp-btn {
  height: 46px !important;
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0.2px;
}
.fp-link {
  color: #0a7ea5;
  text-decoration: underline;
  cursor: pointer;
  font-size: 13px;
}
.fp-link-disabled {
  color: #9e9e9e;
  font-size: 13px;
}
.fp-otp-caption {
  font-size: 13px;
  color: #455a64;
}
.fp-otp-meta {
  font-size: 12px;
  color: #607d8b;
}
.fp-otp-input {
  max-width: 340px;
  margin: 8px auto 0;
}
.fp-desc-body {
  font-size: 13px;
  color: #607d8b;
}
.fp-req-item {
  font-size: 12px;
  color: #9e9e9e;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
}
.fp-req-ok {
  color: #2e7d32;
}
.fp-strength-label {
  font-size: 11px;
  font-weight: 600;
}
.fp-country-select ::v-deep .v-select__selection {
  font-size: 13px;
}
</style>
